import React, { useCallback, useRef, useState } from 'react';

import ClickableIcon from '~components/ClickableIcon';
import Icon, { faPencil, faSave, faSpinner } from '~components/icon';

import { Row } from './types';

interface ComponentProps {
  row: Row;
  onEdit: (row: Row) => void;
  onSaveRow?: (row: Row) => void | Promise<unknown>;
  isEditing?: boolean;
  editonly?: boolean;
  tooltip?: string;
  disabled?: boolean | ((row: Row) => boolean);
}

const EditAction: React.FC<ComponentProps> = ({
  row,
  onEdit,
  onSaveRow,
  isEditing,
  editonly,
  tooltip,
  disabled = false,
}) => {
  const [isSaving, setIsSaving] = useState(false);
  // Guards the window between the click and the first re-render, which state cannot: two clicks in
  // the same tick both read `isSaving === false` and both fire the save.
  const inFlight = useRef(false);
  // A row can unmount while its save is still resolving (a refetch reorders or drops it), and
  // setting state on the way back out warns and, in tests, fails.
  const mounted = useRef(true);
  React.useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const handleSave = useCallback(async () => {
    if (!onSaveRow || inFlight.current) return;

    try {
      // Inside the try: a handler that throws synchronously (rather than returning a rejected
      // promise) must land on the same save-failure path as an async rejection, or it bypasses the
      // catch below entirely and surfaces as an unhandled rejection on this function's own promise.
      const result = onSaveRow(row);

      // A handler that returns nothing keeps the original behaviour exactly: leave edit mode
      // straight away, no spinner. Only a handler that hands back a promise can be waited on, and
      // only then is there anything to wait FOR.
      if (!result || typeof (result as Promise<unknown>).then !== 'function') {
        onEdit(row);
        return;
      }

      inFlight.current = true;
      setIsSaving(true);

      await result;
      // Leaving edit mode is the confirmation that the save landed, so it happens here and not
      // before. Previously the row flipped to read-only on click and a rejected save left the
      // screen showing a value the server had refused.
      if (mounted.current) onEdit(row);
    } catch {
      // Swallowed on purpose: the row stays in edit with its unsaved values intact so the user can
      // correct and retry. Reporting the failure is the caller's job - it is the only layer that
      // knows what the save meant and where its error belongs on screen.
    } finally {
      inFlight.current = false;
      if (mounted.current) setIsSaving(false);
    }
  }, [onEdit, onSaveRow, row]);

  if (editonly) return null;

  if (isSaving) {
    // Deliberately carries ClickableIcon's own wrapper and glyph classes rather than a plain
    // flex box: the spinner stands in for the save icon, so it has to occupy the identical
    // 35x35 box with the same 15x15 glyph, or the actions column shifts by a few pixels for as
    // long as the row is saving. `is-static` drops the pointer cursor and hover highlight, which
    // are the only parts that should not carry over.
    return (
      <div
        className="clickable-icon clickable-small icon-round is-static"
        data-cy={`${row.id}-saving`}
        role="status"
        aria-label="Saving"
      >
        <Icon icon={faSpinner} spin className="icon-small text-success" />
      </div>
    );
  }

  return onSaveRow && isEditing ? (
    <ClickableIcon
      id={`${row.id}-save`}
      icon={faSave}
      size="small"
      color="success"
      onClick={handleSave}
      // Both controls are icon-only `role="button"` elements, so without a label they reach
      // assistive technology unnamed - axe flags it as aria-command-name. The edit icon's tooltip
      // does not supply a name: it is a separate portaled element, not an accessible label.
      ariaLabel="Save row"
    />
  ) : (
    <ClickableIcon
      id={`${row.id}-edit`}
      icon={faPencil}
      size="small"
      color="secondary"
      onClick={() => onEdit(row)}
      disabled={typeof disabled === 'function' ? disabled(row) : disabled}
      toolTip={tooltip ?? 'Toggle Edit'}
      ariaLabel="Edit row"
    />
  );
};

export default EditAction;
